// --- Core
import { SORT_DESC } from './SortHelper.js';

/**
 * @type {string} Abbreviation for all positions (empty string)
 */
const ALL = '';

/**
 * @type {string} Abbreviation for Quarterback position
 */
export const QB = 'QB';

/**
 * @type {string} Abbreviation for Team Quarterback position
 */
const TQB = 'TQB';

/**
 * @type {string} Abbreviation for Running Back position
 */
export const RB = 'RB';

/**
 * @type {string} Abbreviation for Wide Receiver position
 */
export const WR = 'WR';

/**
 * @type {string} Abbreviation for Tight End position
 */
export const TE = 'TE';

/**
 * @type {string} Abbreviation for Kicker position
 */
const K = 'K';

/**
 * @type {string} Abbreviation for Team Kicker position
 */
const TK = 'TK';
/**
 * @type {string} Abbreviation for Defense position
 */
const DEF = 'DEF';

/**
 * @type {string} Abbreviation for Defensive Line position
 */
const DL = 'DL';

/**
 * @type {string} Abbreviation for Defensive Tackle position
 */
const DT = 'DT';

/**
 * @type {string} Abbreviation for Defensive End position
 */
const DE = 'DE';

/**
 * @type {string} Abbreviation for Linebacker position
 */
const LB = 'LB';

/**
 * @type {string} Abbreviation for Defensive Back position
 */
const DB = 'DB';

/**
 * @type {string} Abbreviation for Cornerback position
 */
const CB = 'CB';

/**
 * @type {string} Abbreviation for Safety position
 */
const S = 'S';

/**
 * @type {string} Abbreviation for Head Coach position
 */
const HC = 'HC';

/**
 * @type {string} Abbreviation for Flex position
 */
const FLEX = 'FLEX';

/**
 * @type {string} Abbreviation for Individual Defensive Player position
 */
const IDP = 'IDP';

/**
 * @var array Positions that are aggregates of all players on a team at
 * that position
 */
const TEAM_POSITIONS = [TQB, TK];

/**
 * @var array Keys are the team positions and values ar ethe position of
 * individual players that make up that team position
 */
const TEAM_POSITIONS_MAP = {
    [TQB]: 'QB',
    [TK]: 'K',
};

/**
 * @var array list of position options
 */
const FANTASY_POSITIONS = [
    { abbr: QB, label: 'Quarterback' },
    { abbr: TQB, label: 'Team Quarterback' },
    { abbr: RB, label: 'Running Back' },
    { abbr: WR, label: 'Wide Receiver' },
    { abbr: TE, label: 'Tight End' },
    { abbr: K, label: 'Kicker' },
    { abbr: TK, label: 'Team Kicker' },
    { abbr: DEF, label: 'Team Defense' },
    { abbr: DL, label: 'Defensive Line' },
    { abbr: LB, label: 'Linebacker' },
    { abbr: DB, label: 'Defensive Back' },
    { abbr: HC, label: 'Head Coach' },
];

/**
 * @type {array} Array of objects that have a `value` key that is the position
 * abbreviation and `label` key that is the readable position name.
 */
export const ALL_POSITION_OPTIONS = [
    { value: QB, label: 'Quarterback' },
    { value: RB, label: 'Running Back' },
    { value: WR, label: 'Wide Receiver' },
    { value: TE, label: 'Tight End' },
    { value: K, label: 'Kicker' },
    { value: DEF, label: 'Defense' },
    { value: DL, label: 'Defensive Line' },
    { value: DT, label: 'Defensive Tackle' },
    { value: DE, label: 'Defensive End' },
    { value: LB, label: 'Linebacker' },
    { value: DB, label: 'Defensive Back' },
    { value: CB, label: 'Cornerback' },
    { value: S, label: 'Safety' },
    { value: HC, label: 'Head Coach' },
    { value: TQB, label: 'Team Quarterback' },
    { value: TK, label: 'Team Kicker' },
];

/**
 * @type {array} Fantasy position abbreviations in preferred order
 */
const PREFERRED_ORDER = FANTASY_POSITIONS.map(fp => fp.abbr);

/**
 * @return array Abbreviations for all fantasy positions
 */
const getFantasyPositions = function () {
    return getNonIdpFantasyPositions().concat(getIdpFantasyPositions());
};

/**
 * @return array Abbreviations for IDP fantasy positions
 */
const getIdpFantasyPositions = function () {
    return [DL, LB, DB];
};

/**
 * @return array Abbreviations for non-IDP fantasy positions
 */
const getNonIdpFantasyPositions = function () {
    return [QB, TQB, RB, WR, TE, K, TK, DEF, HC];
};

/**
 * Sorts an array of the (string) positions in the preferred order.
 * @param {array<string>} positions
 * @returns {void}
 */
export const sortPositionsByPreferredOrder = positions => {
    positions.sort((a, b) => {
        return PREFERRED_ORDER.indexOf(a) - PREFERRED_ORDER.indexOf(b);
    });
};

/**
 * Sorts arrays of objects that have a property with a fantasy
 * position by our preferred order of fantasy positions
 * @param {array<object>} array The array being sorted
 * @param {string} positionPropertyName The property on the objects to get the
 * position by
 * @param {object} options An optional object accepting options
 * @property {string} options.secondarySortProperty A secondary property to sort
 * @property {string} options.secondarySortDirection A direction to sort for the
 * secondary property
 */
export const sortByPreferredPositionOrder = function (array, positionPropertyName = 'fantasy_position', options = {}) {
    const { secondarySortProperty, secondarySortDirection } = {
        // --- Default to preserve assignments in mock drafts w/picks
        secondarySortProperty: false,
        secondarySortDirection: SORT_DESC,
        ...options,
    };
    array.sort((a, b) => {
        const aSortVal = PREFERRED_ORDER.indexOf(a[positionPropertyName]);
        const bSortVal = PREFERRED_ORDER.indexOf(b[positionPropertyName]);
        if (aSortVal != bSortVal || !secondarySortProperty) {
            return aSortVal - bSortVal;
        }
        const aSecondaryValue = a[secondarySortProperty];
        const bSecondaryValue = b[secondarySortProperty];
        const secondarySortValue = aSecondaryValue - bSecondaryValue;
        return secondarySortDirection == SORT_DESC ? -1 * secondarySortValue : secondarySortValue;
    });
};

/**
 * Returns an object where the properties are fantasy positions.
 * @param {any} value The value to assign to each property of the object
 * @returns {object}
 */
export const getFantasyPositionKeyedObject = value => {
    return FANTASY_POSITIONS.reduce((acc, item, _index) => {
        acc[item.abbr] = Array.isArray(value)
            ? [...value] // --- Avoid passing array by reference
            : value;
        return acc;
    }, {});
};

/**
 * Splits "DL,DT,DE" into its individual parts
 *
 * @param {string|null} position
 * @returns {array<string>}
 */
const splitPositions = function (position) {
    if (!position) {
        return [];
    }
    return String(position)
        .toUpperCase()
        .split(',')
        .map(part => part.trim())
        .filter(Boolean);
};

/**
 * Converts variations of the same position into a single value we recognize
 *
 * @param {string|null} position
 * @returns {string|null}
 */
export const normalizePosition = function (position) {
    const positions = splitPositions(position);

    if (!positions.length) {
        return null;
    }

    position = positions[0];

    switch (position) {
        case 'D/ST':
        case 'DST':
        case 'ST':
        case 'DF':
            return 'DEF';
        case 'PN':
            return 'P';
        case 'PK':
            return 'K';
        case 'TMPK':
            return 'TK';
        case 'TMQB':
            return 'TQB';
        case 'HEAD':
        case 'COACH':
            return 'HC';
        case 'ILB':
        case 'OLB':
            return 'LB';
        case 'DB/LB/DL':
        case 'D':
        case 'DP':
        case 'IDP':
        case 'IDP_FLEX':
            return 'DB';
        case 'QB-RB-WR-TE':
        case 'QB/RB/WR/TE':
        case 'QB/WR/RB/TE':
        case 'Q/W/R/T':
        case 'Q/R/W/T':
        case 'QRWT':
        case 'QWRT':
        case 'OP':
        case 'SFLEX':
        case 'SUPER_FLEX':
        case 'SUPERFLEX':
        case 'SFX':
        case 'SFX2':
        case 'QB2':
        case 'QB/RB/WR':
            return 'QB';
        case 'RB-WR-TE':
        case 'WR-RB-TE':
        case 'RB/WR/TE':
        case 'WR/RB/TE':
        case 'RB/WR/TE/K':
        case 'W/R/T':
        case 'R/W/T':
        case 'WRT':
        case 'RWT':
        case 'FLEX':
        case 'RB-WR':
        case 'RB/WR':
        case 'WR/RB':
        case 'WRRB_FLEX':
        case 'RB2':
        case 'W/R':
        case 'R/W':
        case 'RW':
            return 'RB';
        case 'WR-TE':
        case 'WR/TE':
        case 'WT':
        case 'REC_FLEX':
        case 'WR2':
            return 'WR';
        default:
            return position;
    }
};

/**
 * Converts variations of the same slot into a single value we recognize
 *
 * @param {string|null} position
 * @returns {string|null}
 */
export const normalizeSlotPosition = function (position) {
    if (!position) {
        return null;
    }

    position = String(position).toUpperCase();

    switch (position) {
        case 'QB-RB-WR-TE':
        case 'QB/RB/WR/TE':
        case 'QB-WR-RB-TE':
        case 'QB/WR/RB/TE':
        case 'Q-R-W-T':
        case 'Q/R/W/T':
        case 'Q-W-R-T':
        case 'Q/W/R/T':
        case 'QWRT':
        case 'QRWT':
        case 'OP':
        case 'SFLEX':
        case 'SUPER_FLEX':
        case 'SUPERFLEX':
        case 'SFX':
        case 'SFX2':
        case 'QB-RB-WR':
        case 'QB/RB/WR':
        case 'QB-WR-RB':
        case 'QB/WR/RB':
        case 'Q-R-W':
        case 'Q/R/W':
        case 'Q-W-R':
        case 'Q/W/R':
        case 'QRW':
        case 'QWR':
            return 'Q|R|W|T';
        case 'RB-WR-TE':
        case 'WR-RB-TE':
        case 'RB/WR/TE':
        case 'WR/RB/TE':
        case 'RB/WR/TE/K':
        case 'R-W-T':
        case 'W-R-T':
        case 'R/W/T':
        case 'W/R/T':
        case 'R-W-T':
        case 'W-R-T':
        case 'RWT':
        case 'WRT':
        case 'FLEX':
        case 'FLX':
            return 'R|W|T';
        case 'WR-TE':
        case 'WR/TE':
        case 'W-T':
        case 'W/T':
        case 'WT':
        case 'REC_FLEX':
            return 'W|T';
        case 'RB-WR':
        case 'RB/WR':
        case 'WR-RB':
        case 'WR/RB':
        case 'RW':
        case 'R-W':
        case 'R/W':
        case 'W-R':
        case 'W/R':
        case 'WRRB_FLEX':
            return 'R|W';
        case 'DB/LB/DL':
        case 'DB-LB-DL':
        case 'DL/LB/DB':
        case 'DL-LB-DB':
        case 'DP':
        case 'IDP':
        case 'IDP_FLEX':
        case 'D':
        case 'ID':
        case 'LBDL':
        case 'DLDB':
        case 'ER':
        case 'ERT':
        case 'LDB':
            return 'D|L|D';
        case 'TX':
            return 'TAXI';
        case 'BENCH':
        case 'BE':
        case 'RS':
            return 'BN';
        case 'I':
            return 'IR';
        case 'EDR':
            return 'DL';
        default:
            // Host slots like S/CB must match DS roster spots (DB)
            return normalizeFantasyPosition(position) || normalizePosition(position);
    }
};

/**
 * Returns the `fantasy_position` abbreviation used on this site based on many
 * different potential position abbreviations from the commissioning services
 *
 * @param {string|null} fantasyPosition
 * @returns {string|null}
 */
const normalizeFantasyPosition = function (fantasyPosition) {
    const fantasyPositions = splitPositions(fantasyPosition);

    if (!fantasyPositions.length) {
        return null;
    }

    switch (fantasyPositions[0]) {
        case 'DEF':
        case 'D':
        case 'DF':
        case 'ST':
        case 'D/ST':
        case 'DST':
        case 'TM':
        case 'TMD':
        case 'TD2':
            return 'DEF';
        case 'QB':
        case 'SFLEX':
        case 'OP':
            return 'QB';
        case 'WR':
        case 'WR-TE':
        case 'XYZ':
            return 'WR';
        case 'TE':
            return 'TE';
        case 'RB-WR':
        case 'RB-WR-TE':
        case 'RB':
        case 'FB':
        case 'HB':
        case 'TB':
        case 'LH':
        case 'RH':
        case 'BB':
        case 'B':
            return 'RB';
        // --- Kicker
        case 'PK':
        case 'K':
            return 'K';
        case 'TMPK':
        case 'TK':
            return 'TK';
        // --- Punter
        case 'PN':
        case 'P':
            return 'P';
        case 'TMP':
        case 'TP':
            return 'TP';
        case 'TMQB':
        case 'TQB':
            return 'TQB';
        case 'HEAD':
        case 'COACH':
        case 'HC':
            return 'HC';
        case 'DE':
        case 'DT':
        case 'NT':
        case 'DL':
        case 'EDR':
            return 'DL';
        case 'CB':
        case 'S':
        case 'SS':
        case 'RS':
        case 'LS':
        case 'DB':
            return 'DB';
        case 'ILB':
        case 'OLB':
        case 'LB':
        case 'IL':
        case 'OL':
        case 'LS':
        case 'TO':
            return 'LB';
        default:
            return null;
    }
};

/**
 * Returns true if a position is IDP
 * @param {string} position
 * @returns {boolean}
 */
export const positionIsIdp = function (position) {
    const [firstPosition] = splitPositions(position);
    return [DL, DE, DT, DB, S, CB, LB].includes(firstPosition);
};

export default {
    ALL,
    QB,
    TQB,
    RB,
    WR,
    TE,
    IDP,
    K,
    TK,
    DEF,
    DL,
    DT,
    DE,
    LB,
    DB,
    CB,
    S,
    HC,
    TEAM_POSITIONS: TEAM_POSITIONS,
    TEAM_POSITIONS_MAP: TEAM_POSITIONS_MAP,
    FANTASY_POSITIONS: FANTASY_POSITIONS,
    getFantasyPositions,
    getIdpFantasyPositions,
    getNonIdpFantasyPositions,
    sortByPreferredPositionOrder,
    /**
     * @return String full name of the position
     */
    getPositionName: function (positionAbbr) {
        switch (positionAbbr.toUpperCase()) {
            case QB:
                return 'Quarterback';
            case RB:
                return 'Running Back';
            case WR:
                return 'Wide Receiver';
            case TE:
                return 'Tight End';
            case K:
                return 'Kicker';
            case DEF:
                return 'Team Defense';
            case DL:
                return 'Defensive Line';
            case LB:
                return 'Linebacker';
            case DB:
                return 'Defensive Back';
            case HC:
                return 'Head Coach';
            case TQB:
                return 'Team Quarterback';
            case TK:
                return 'Team Kicker';
            case FLEX:
                return 'Flex';
            case IDP:
                return 'IDP';
            default:
                return positionAbbr;
        }
    },
    isIdp: positionIsIdp,
    normalizePosition,
    normalizeSlotPosition,
    normalizeFantasyPosition,
};
